import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang, LANGS } from "@/i18n";

const linkDefs = [
  { href: "#apropos", key: "nav.about" },
  { href: "#services", key: "nav.services" },
  { href: "#talents", key: "nav.talents" },
  { href: "#actualites", key: "nav.news" },
  { href: "#contact", key: "nav.contact" },
];

function LangSwitcher({ className }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 ${className || ""}`} data-testid="lang-switcher">
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          data-testid={`lang-${l.code}`}
          className={`px-2 py-1 text-[10px] tracking-[0.15em] transition-colors duration-300 ${
            lang === l.code ? "text-[#C5A059] border-b border-[#C5A059]" : "text-[#71717A] hover:text-white"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [plus, setPlus] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 border-b ${
        scrolled ? "bg-black/60 backdrop-blur-xl border-white/10" : "bg-black/30 backdrop-blur-md border-white/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <a href="#hero" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src="/assets/logo-dark-sm.png" alt="DREAM2REALITY Football Agency" className="h-11 w-auto" />
        </a>

        <nav className="hidden lg:flex items-center gap-3 2xl:gap-5">
          {linkDefs.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.key.split(".")[1]}`}
              className="relative text-[11px] uppercase tracking-[0.18em] whitespace-nowrap text-[#A1A1AA] hover:text-white transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#C5A059] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {t(l.key)}
            </a>
          ))}
          <a
            href="/equipe"
            data-testid="nav-link-equipe"
            className="relative text-[11px] uppercase tracking-[0.18em] whitespace-nowrap text-[#A1A1AA] hover:text-white transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#C5A059] after:transition-[width] after:duration-300 hover:after:w-full"
          >
            {t("nav.equipe")}
          </a>
          <a
            href="/detection"
            data-testid="nav-link-detection"
            className="relative text-[11px] uppercase tracking-[0.18em] whitespace-nowrap text-[#A1A1AA] hover:text-white transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#C5A059] after:transition-[width] after:duration-300 hover:after:w-full"
          >
            {t("nav.detection")}
          </a>
          <div className="relative" onMouseEnter={() => setPlus(true)} onMouseLeave={() => setPlus(false)}>
            <button data-testid="nav-plus" className="flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] whitespace-nowrap text-[#A1A1AA] hover:text-white transition-colors duration-300">
              {t("nav.plus")} <ChevronDown size={12} className={`transition-transform duration-300 ${plus ? "rotate-180" : ""}`} />
            </button>
            {plus && (
              <div data-testid="nav-plus-menu" className="absolute right-0 top-full pt-3 z-50">
                <div className="bg-[#121214] border border-[#27272A] min-w-[220px] py-2 shadow-2xl">
                  <a href="/mur-des-legendes" data-testid="nav-link-mur-legendes" className="block px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-[#C5A059] hover:bg-[#C5A059]/5 transition-colors">{t("nav.mur")}</a>
                  <a href="/ressources" data-testid="nav-link-ressources" className="block px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#C5A059] hover:bg-[#C5A059]/5 transition-colors">{t("nav.ressources")}</a>
                  <a href="/carrieres" data-testid="nav-link-carrieres" className="block px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#C5A059] hover:bg-[#C5A059]/5 transition-colors">{t("nav.jobs")}</a>
                  <a href="/comment-s-inscrire" data-testid="nav-link-guide" className="block px-5 py-3 text-[11px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#C5A059] hover:bg-[#C5A059]/5 transition-colors">{t("nav.guide")}</a>
                </div>
              </div>
            )}
          </div>
          <LangSwitcher />
          <a
            href="/detection"
            data-testid="nav-cta-detection"
            className="border border-[#C5A059] text-[#C5A059] px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] whitespace-nowrap hover:bg-[#C5A059] hover:text-black transition-colors duration-300"
          >
            {t("nav.cta")}
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0A0A0C]/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {linkDefs.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-sm uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#C5A059] transition-colors"
                >
                  {t(l.key)}
                </a>
              ))}
              <a
                href="/equipe"
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#C5A059] transition-colors"
              >
                {t("nav.equipe")}
              </a>
              <a
                href="/detection"
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#C5A059] transition-colors"
              >
                {t("nav.detection")}
              </a>
              <a
                href="/mur-des-legendes"
                data-testid="nav-mobile-link-mur"
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.2em] text-[#C5A059] hover:text-[#E5C07B] transition-colors"
              >
                {t("nav.mur")}
              </a>
              <a
                href="/ressources"
                data-testid="nav-mobile-link-ressources"
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#C5A059] transition-colors"
              >
                {t("nav.ressources")}
              </a>
              <a
                href="/carrieres"
                data-testid="nav-mobile-link-carrieres"
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#C5A059] transition-colors"
              >
                {t("nav.jobs")}
              </a>
              <a
                href="/comment-s-inscrire"
                data-testid="nav-mobile-link-guide"
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.2em] text-[#C5A059] hover:text-[#E5C07B] transition-colors"
              >
                {t("nav.guide")}
              </a>
              <LangSwitcher className="pt-2" />
              <a
                href="/detection"
                onClick={() => setOpen(false)}
                className="border border-[#C5A059] text-[#C5A059] px-6 py-3 text-xs uppercase tracking-[0.2em] text-center"
              >
                {t("nav.cta")}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
